
import { useState, useEffect } from 'react';
import { Category } from '../types';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface UseCategoryFormProps {
  currentCategory: Partial<Category> | null;
  isEditing: boolean;
  onSuccess: () => void;
  onClose: () => void;
}

export const useCategoryForm = ({ currentCategory, isEditing, onSuccess, onClose }: UseCategoryFormProps) => {
  const [category, setCategory] = useState<Partial<Category> | null>(null);

  useEffect(() => {
    setCategory(currentCategory);
  }, [currentCategory]);

  const generateSlug = (name: string) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  };

  const handleNameChange = (name: string) => {
    if (!category) return;
    
    if (!isEditing || !category.slug) {
      setCategory({
        ...category,
        name,
        slug: generateSlug(name)
      });
    } else {
      setCategory({
        ...category,
        name
      });
    }
  };

  const handleSaveCategory = async () => {
    if (!category || !category.name || !category.slug) {
      toast.error('Name and slug are required');
      return;
    }

    try {
      if (isEditing) {
        const { id, created_at, updated_at, ...updateData } = category as Category;
        
        const { error } = await supabase
          .from('insight_categories')
          .update(updateData)
          .eq('id', id);
          
        if (error) throw error;
        toast.success('Category updated successfully');
      } else {
        const categoryToInsert = {
          name: category.name,
          slug: category.slug,
          description: category.description,
          active: category.active ?? true
        };
        
        const { error } = await supabase
          .from('insight_categories')
          .insert(categoryToInsert);
          
        if (error) throw error;
        toast.success('Category created successfully');
      }
      
      onClose();
      onSuccess();
    } catch (error: any) {
      console.error('Error saving category:', error);
      toast.error(error.message || 'Failed to save category');
    }
  };

  return {
    category,
    setCategory,
    handleNameChange,
    handleSaveCategory
  };
};
